import React, { FC, useCallback, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from './Tips'
import { compile } from '../../../utils/search'
import { sortBy } from 'lodash'

interface Member {
  id: number
  name: string
  kana: string | null
  email: string
  code: string | null
  team_name: string,
  profile_image: string | null
  active: boolean
  use_common_setting: boolean
  value: boolean | string | number | null
  platform?: {
    windows: boolean
    macos: boolean
  }
}

interface Props {
  originMemberList: Member[]
  statusList: {
    label: string,
    value: boolean | string | number,
  }[]
  commonValue: boolean | string | number
  valueLabel: string
  type: string
  modalOpenCallback: (memberIds: number[]) => void
}

const DISPLAY_BLOCK_NUMBER = 100

const MemberListModule: FC<Props> = (props) => {
  const {
    originMemberList,
    statusList,
    commonValue,
    valueLabel,
    type,
    modalOpenCallback,
  } = props

  const [checkedMemberIds, setCheckedMemberIds] = useState<number[]>([])

  const [memberList, setMemberList] = useState<Member[]>([])

  const [composition, setComposition] = useState<boolean>(false)
  const [searchWord, setSearchWord] = useState<string>("")

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && memberList.length >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
  }, [displayBlockNum, memberList])

  const clickCheckbox = (memberId: number) => {
    if (checkedMemberIds.includes(memberId)) {
      setCheckedMemberIds(checkedMemberIds.filter((v) => v !== memberId))
    } else {
      setCheckedMemberIds([...checkedMemberIds, memberId])
    }
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const reset = () => {
    setSearchWord("")
    setMemberList(originMemberList)
  }

  const search = (originalList: Member[] | undefined) => {
    if (!searchWord) {
      reset()
      setDisplayBlockNum(1)
      return
    }
    const word = searchWord.toLowerCase().split(/\s+/)
    const list = originalList?.filter((v) => {
      if (v === null) return false
      return (
        compile(word).test(v.name.toLowerCase() + v.kana?.toLowerCase() + v.code?.toLowerCase() + v.email?.toLowerCase() + v.team_name?.toLowerCase())
      )
    }) as Member[]
    setMemberList(sortBy(list, 'id'))
    const memberIds = list.map((v) => v.id)
    setCheckedMemberIds((cur) => cur.filter((v) => memberIds.includes(v)))
    setDisplayBlockNum(1)
  }

  const modalOpen = (memberIds: number[]) => {
    modalOpenCallback(memberIds)
  }

  useEffect(() => {
    setMemberList(originMemberList)
  }, [originMemberList])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              ユーザーの個別設定
            </div>
            <div className="search_area">
              <input
                type="text"
                placeholder="名前・メールアドレス・チーム名でユーザー検索"
                onChange={(event) => setSearchWord(event.target.value)}
                onBlur={() => search(originMemberList)}
                onKeyDown={(event) => keyDown(event)}
                onCompositionStart={() => setComposition(true)}
                onCompositionEnd={() => setComposition(false)}
                value={searchWord}
                style={{ width: "300px" }}
              />
              {
                searchWord && (
                  <button type="button" className="reset" onClick={reset}>
                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                  </button>
                )
              }
            </div>
          </div>
          <div className="block-detail">
            <div
              style={{
                border: `solid 1px ${checkedMemberIds.length ? "#228b22" : "#999999"}`,
                borderRadius: "4px",
                backgroundColor: checkedMemberIds.length ? "#f3fff9" : "#EEEEEE",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "right",
                gap: "12px",
                marginBottom: "16px",
                position: "sticky",
                top: "60px",
                zIndex: "1",
              }}
            >
              {
                checkedMemberIds.length ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "2px",
                      }}
                    >
                      <div
                        style={{
                          color: "#228b22",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {checkedMemberIds.length}
                      </div>
                      <div>
                        名のユーザーが選択されています
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => modalOpen(checkedMemberIds)}
                      style={{
                        border: "1px solid #228b22",
                        borderRadius: "4px",
                        backgroundColor: "#228b22",
                        color: "#FFFFFF",
                        display: "flex",
                        gap: "4px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={["fas", "check"]}
                        fixedWidth
                      />
                      まとめて変更する
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        marginTop: "8px",
                      }}
                    >
                      ユーザーが選択されていません
                    </div>
                    <button
                      type="button"
                      disabled
                      style={{
                        border: "1px solid #DDDDDD",
                        borderRadius: "4px",
                        backgroundColor: "#DDDDDD",
                        color: "#999999",
                        display: "flex",
                        gap: "4px",
                        cursor: "not-allowed",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={["fas", "check"]}
                        fixedWidth
                      />
                      まとめて変更する
                    </button>
                  </>
                )
              }
            </div>
            {
              memberList.length ? (
                <>
                  <table className="flex-table">
                    <thead>
                      <tr>
                        <th style={{ width: "30px" }}>
                          <div
                            style={{
                              fontSize: "16px",
                              // eslint-disable-next-line max-len
                              color: (memberList.some((v) => v.platform) && checkedMemberIds.length === memberList.filter((v) => v.platform?.windows).length) || checkedMemberIds.length === memberList.length ? "#008800" : "#666666",
                              position: "relative",
                              cursor: "pointer",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "8px",
                                color: "#999999",
                                whiteSpace: "nowrap",
                                position: "absolute",
                                top: "-10px",
                                left: "50%",
                                transform: "translateX(-50%)",
                              }}
                            >
                              全選択
                            </div>
                            {
                              // eslint-disable-next-line max-len
                              (memberList.some((v) => v.platform) && checkedMemberIds.length === memberList.filter((v) => v.platform?.windows).length) || checkedMemberIds.length === memberList.length ? (
                                <FontAwesomeIcon icon={['far', 'check-square']} onClick={() => setCheckedMemberIds([])} />
                              ) : (
                                <FontAwesomeIcon
                                  icon={['far', 'square']}
                                  onClick={() => {
                                    if (memberList.some((v) => v.platform)) {
                                      setCheckedMemberIds(memberList.filter((v) => v.platform?.windows).map((v) => v.id))
                                    } else {
                                      setCheckedMemberIds(memberList.map((v) => v.id))
                                    }
                                  }}
                                />
                              )
                            }
                          </div>
                        </th>
                        <th style={{ width: "50px", justifyContent: "center" }}>
                          操作
                        </th>
                        <th style={{ width: "20%", justifyContent: "center" }}>
                          名前
                        </th>
                        <th style={{ width: "20%", justifyContent: "center" }}>
                          メールアドレス
                        </th>
                        <th style={{ width: "20%", justifyContent: "center" }}>
                          チーム
                        </th>
                        {
                          memberList.some((v) => !!v.platform) && (
                            <th style={{ width: "10%", justifyContent: "center" }}>
                              OS
                            </th>
                          )
                        }
                        <th style={{ width: `calc(${memberList.some((v) => !!v.platform) ? "30%" : "40%"} - 80px)`, justifyContent: "center" }}>
                          {valueLabel}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        memberList.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((member) => (
                          <tr
                            key={`${member.id}_${member.value}`}
                            role="none"
                            onClick={() => (!member.platform || member.platform.windows) && clickCheckbox(member.id)}
                            style={{
                              backgroundColor: checkedMemberIds.includes(member.id) ? "#f3fff9" : "",
                              cursor: "pointer",
                            }}
                          >
                            <td style={{ width: "30px" }}>
                              {
                                (!member.platform || member.platform.windows) && (
                                  <div
                                    style={{
                                      fontSize: "16px",
                                      color: checkedMemberIds.includes(member.id) ? "#008800" : "#666666",
                                    }}
                                  >
                                    {
                                      checkedMemberIds.includes(member.id) ? (
                                        <FontAwesomeIcon icon={['far', 'check-square']} />
                                      ) : (
                                        <FontAwesomeIcon icon={['far', 'square']} />
                                      )
                                    }
                                  </div>
                                )
                              }
                            </td>
                            <td style={{ width: "50px", justifyContent: "center" }}>
                              {
                                (!member.platform || member.platform.windows) ? (
                                  <Tips
                                    description="編集"
                                    className=""
                                    icon={false}
                                  >
                                    <button
                                      className="edit"
                                      type="button"
                                      onClick={(e) => {
                                        modalOpen([member.id])
                                        e.stopPropagation()
                                      }}
                                      style={{
                                        color: "#666666",
                                        fontSize: "20px",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'tools']} />
                                    </button>
                                  </Tips>
                                ) : (
                                  <Tips
                                    description="設定不可OSの為、設定できません"
                                    className=""
                                    icon={false}
                                  >
                                    <div
                                      style={{
                                        color: "#CCCCCC",
                                        fontSize: "20px",
                                        padding: "8px 12px",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'tools']} />
                                    </div>
                                  </Tips>
                                )
                              }
                            </td>
                            <td style={{ width: "20%", justifyContent: "center", gap: "4px" }}>
                              <img
                                alt={member.name}
                                src={member.profile_image ?? `/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                              />
                              <ruby>
                                {member.name}
                                <rt>{member.kana}</rt>
                              </ruby>
                              {member.code ? ` (${member.code})` : ""}
                              {
                                member.active || (
                                  <div>
                                    <Tips
                                      description="休眠中💤"
                                      className=""
                                      icon={false}
                                      style={{
                                        position: "relative",
                                      }}
                                    >
                                      <>
                                        💤
                                      </>
                                    </Tips>
                                  </div>
                                )
                              }
                            </td>
                            <td style={{ width: "20%", justifyContent: "center" }}>
                              <div
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  minWidth: 0,
                                  textAlign: "center",
                                }}
                              >
                                {member.email}
                              </div>
                            </td>
                            <td style={{ width: "20%", justifyContent: "center" }}>
                              <div
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  minWidth: 0,
                                  textAlign: "center",
                                }}
                              >
                                {member.team_name || "所属チームなし"}
                              </div>
                            </td>
                            {
                              !!member.platform && (
                                <td style={{ width: "10%", justifyContent: "center" }}>
                                  <div
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: "100%",
                                      minWidth: 0,
                                      textAlign: "center",
                                    }}
                                  >
                                    {
                                      member.platform.windows && (
                                        <div>Windows</div>
                                      )
                                    }
                                    {
                                      member.platform.macos && (
                                        <div>Mac</div>
                                      )
                                    }
                                    {
                                      (!member.platform.windows && !member.platform.macos) && (
                                        <div>-</div>
                                      )
                                    }
                                  </div>
                                </td>
                              )
                            }
                            <td style={{ width: `calc(${member.platform ? "30%" : "40%"} - 80px)`, justifyContent: "center" }}>
                              <div
                                style={{
                                  display: "flex",
                                  flexFlow: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: "2px",
                                }}
                              >
                                {
                                  !!member.platform && !member.platform?.windows ? (
                                    <>
                                      <div style={{ color: "#666666", fontSize: "10px" }}>
                                        設定不可OSの為
                                      </div>
                                      <div style={{ color: "#FF0000", fontSize: "16px", fontWeight: "bold" }}>
                                        設定できません
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      {
                                        member.use_common_setting && (
                                          <div style={{ color: "#666666", fontSize: "10px" }}>
                                            会社全体の設定に準拠する
                                          </div>
                                        )
                                      }
                                      <div style={{ fontSize: "16px" }}>
                                        {
                                          type === "bool" && (
                                            <>
                                              {
                                                ((member.use_common_setting && commonValue) || (!member.use_common_setting && member.value)) ? (
                                                  <span style={{ color: "#008800", fontWeight: "bold" }}>
                                                    {statusList.find((v) => v.value)?.label}
                                                  </span>
                                                ) : (
                                                  <span style={{ color: "#008800", fontWeight: "bold" }}>
                                                    {statusList.find((v) => !v.value)?.label}
                                                  </span>
                                                )
                                              }
                                            </>
                                          )
                                        }
                                        {
                                          ["string", "number"].includes(type) && (
                                            <>
                                              {
                                                (member.use_common_setting) ? (
                                                  <span style={{ color: "#008800", fontWeight: "bold" }}>
                                                    {statusList.find((v) => v.value === commonValue)?.label}
                                                  </span>
                                                ) : (
                                                  <span style={{ color: "#008800", fontWeight: "bold" }}>
                                                    {statusList.find((v) => v.value === member.value)?.label}
                                                  </span>
                                                )
                                              }
                                            </>
                                          )
                                        }
                                        {
                                          type === "direct" && (
                                            <>
                                              {
                                                (member.use_common_setting) ? (
                                                  <span style={{ color: "#008800", fontWeight: "bold" }}>
                                                    {commonValue}
                                                  </span>
                                                ) : (
                                                  <span style={{ color: "#008800", fontWeight: "bold" }}>
                                                    {member.value}
                                                  </span>
                                                )
                                              }
                                            </>
                                          )
                                        }
                                      </div>
                                    </>
                                  )
                                }
                              </div>
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </>
              ) : (
                <>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#999999",
                    }}
                  >
                    対象がありません
                  </div>
                </>
              )
            }
          </div>
        </div>
      </section>
    </>
  )
}

export default MemberListModule
